"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type PointerEvent,
} from "react";
import {
  CheckCircle2,
  Download,
  Eraser,
  Eye,
  FileCheck2,
  FileUp,
  LogIn,
  LogOut,
  PenLine,
  Plus,
  RotateCcw,
  Save,
  Smartphone,
  Square,
  Trash2,
  Type,
  X,
  UserRound,
} from "lucide-react";
import { profileFields } from "@/lib/editorFields";
import { BrandLogo } from "@/components/BrandLogo";
import { PdfEditor, type PdfEditorHandle } from "@/components/pdf-editor/PdfEditor";
import { PdfPreviewModal } from "@/components/pdf-editor/PdfPreviewModal";
import { DEMO_MODE_HEADER, demoDownloadBlockedMessage } from "@/lib/demoSecurity";
import { validatePdfUpload } from "@/lib/pdfUpload";
import type { SavedUserProfile } from "@/lib/userProfile";
import type { PdfEditorElement, PdfEditorElementType } from "@/components/pdf-editor/types";

type DashboardClientProps = {
  demoMode?: boolean;
  email: string | null;
  initialExportUnlocked?: boolean;
  initialProfile: SavedUserProfile;
  marketingUrl: string;
  role: string;
  priceLabel: string;
  initialSessionId?: string | null;
};

type UploadState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; fileName: string; sessionId: string; expiresAt: string }
  | { status: "error"; message: string };

type CustomField = {
  id: string;
  label: string;
  value: string;
};

type InsertableField = {
  id: string;
  label: string;
  kind: "text" | "date";
  value: string;
  custom: boolean;
};

type ToastState = {
  id: number;
  title: string;
  detail: string;
};

type SignaturePoint = {
  x: number;
  y: number;
};

const SIGNATURE_INK = "#123b7a";

function getTodayInputValue() {
  const today = new Date();
  const timezoneOffsetMs = today.getTimezoneOffset() * 60 * 1000;
  return new Date(today.getTime() - timezoneOffsetMs).toISOString().slice(0, 10);
}

function createLocalId(prefix: string) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function signatureStrokeToPath(points: SignaturePoint[]) {
  if (points.length === 0) {
    return "";
  }

  if (points.length === 1) {
    return `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)} h 0.01`;
  }

  const commands = [`M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`];

  for (let index = 1; index < points.length - 1; index += 1) {
    const midpoint = {
      x: (points[index].x + points[index + 1].x) / 2,
      y: (points[index].y + points[index + 1].y) / 2,
    };
    commands.push(
      `Q ${points[index].x.toFixed(2)} ${points[index].y.toFixed(2)} ${midpoint.x.toFixed(2)} ${midpoint.y.toFixed(2)}`,
    );
  }

  const last = points[points.length - 1];
  commands.push(`L ${last.x.toFixed(2)} ${last.y.toFixed(2)}`);
  return commands.join(" ");
}

function signatureStrokesToSvg(strokes: SignaturePoint[][], width: number, height: number) {
  const path = strokes.map(signatureStrokeToPath).filter(Boolean).join(" ");
  const safeWidth = Math.max(1, Math.round(width));
  const safeHeight = Math.max(1, Math.round(height));

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${safeWidth} ${safeHeight}" width="${safeWidth}" height="${safeHeight}">`,
    `<path d="${path}" fill="none" stroke="${SIGNATURE_INK}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>`,
    "</svg>",
  ].join("");
}

function svgToDataUrl(svg: string) {
  return `data:image/svg+xml;base64,${window.btoa(svg)}`;
}

export function DashboardClient({
  demoMode = false,
  email,
  initialExportUnlocked = false,
  initialProfile,
  marketingUrl,
  role,
  priceLabel,
  initialSessionId = null,
}: DashboardClientProps) {
  const requestSeq = useRef(0);
  const editorRef = useRef<PdfEditorHandle | null>(null);
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const signatureStrokesRef = useRef<SignaturePoint[][]>([]);
  const activeSignatureStrokeRef = useRef<SignaturePoint[]>([]);
  const drawingSignatureRef = useRef(false);
  const [upload, setUpload] = useState<UploadState>(() => {
    return initialSessionId
      ? {
          status: "ready",
          sessionId: initialSessionId,
          fileName: "Wznowiona sesja",
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        }
      : { status: "idle" };
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(() => {
    return initialSessionId ? `/api/pdf/sessions/${initialSessionId}/source` : null;
  });
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [signatureDrawerOpen, setSignatureDrawerOpen] = useState(false);
  const [signatureHasInk, setSignatureHasInk] = useState(false);
  const [profileValues, setProfileValues] = useState<Record<string, string>>({
    Data: getTodayInputValue(),
    ...initialProfile.profileValues,
  });
  const [customFields, setCustomFields] = useState<CustomField[]>(initialProfile.customFields);
  const [selectedElement, setSelectedElement] = useState<PdfEditorElement | null>(null);
  const [selectionCount, setSelectionCount] = useState(0);
  const [elements, setElements] = useState<PdfEditorElement[]>([]);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [checkoutStatus, setCheckoutStatus] = useState<"idle" | "loading">("idle");
  const [convertedFromDemo, setConvertedFromDemo] = useState(false);
  const [exportUnlocked, setExportUnlocked] = useState(initialExportUnlocked);
  const [adminEmailStatus, setAdminEmailStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [pdfEmailStatus, setPdfEmailStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const sessionId = upload.status === "ready" ? upload.sessionId : null;
  const canStartCheckout = upload.status === "ready";
  const insertableFields = useMemo<InsertableField[]>(() => {
    const baseFields = profileFields.map((field) => ({
      id: field.label,
      label: field.label,
      kind: field.kind,
      value: profileValues[field.label] || "",
      custom: false,
    }));
    const userFields = customFields.map((field) => ({
      id: field.id,
      label: field.label,
      kind: "text" as const,
      value: field.value,
      custom: true,
    }));

    return [...baseFields, ...userFields];
  }, [customFields, profileValues]);

  function loginWithPopup() {
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const callbackUrl = `/login/complete?returnTo=${encodeURIComponent(returnTo)}`;
    const authUrl = `/login/google?callbackUrl=${encodeURIComponent(callbackUrl)}`;

    const popup = window.open(
      authUrl,
      "google-login",
      `width=${width},height=${height},left=${left},top=${top}`
    );

    const interval = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(interval);
        window.location.reload();
      }
    }, 500);
  }

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      if (signatureUrl) {
        URL.revokeObjectURL(signatureUrl);
      }
    };
  }, [signatureUrl]);

  useEffect(() => {
    if (!toast) {
      return;
    }

    const timeout = window.setTimeout(() => setToast(null), 2200);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    if (!email) {
      return;
    }

    const timeout = window.setTimeout(() => {
      void fetch("/api/user/profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customFields, profileValues }),
      });
    }, 700);

    return () => window.clearTimeout(timeout);
  }, [customFields, email, profileValues]);

  async function uploadPdf(file: File) {
    if (!file) {
      return;
    }

    const validationError = validatePdfUpload({
      name: file.name,
      type: file.type,
      size: file.size,
    });

    if (validationError) {
      setUpload({ status: "error", message: validationError });
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl(URL.createObjectURL(file));
    setUpload({ status: "loading" });
    setExportUnlocked(role === "admin");
    setSelectedElement(null);
    setElements([]);

    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch("/api/pdf/upload", {
      method: "POST",
      headers: demoMode ? { [DEMO_MODE_HEADER]: "1" } : undefined,
      body: formData,
    });

    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json") ? await response.json() : null;

    if (!response.ok) {
      setUpload({
        status: "error",
        message: payload?.error || (response.status === 413 ? "PDF moze miec maksymalnie 10 MB." : "Upload nie powiódł się."),
      });
      return;
    }

    setUpload({
      status: "ready",
      fileName: payload.fileName,
      sessionId: payload.sessionId,
      expiresAt: payload.expiresAt,
    });
    
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", demoMode ? `?demo=1&s=${payload.sessionId}` : `?s=${payload.sessionId}`);
    }
  }

  async function handleUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) {
      await uploadPdf(file);
    }
    event.target.value = "";
  }

  async function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      await uploadPdf(file);
    }
  }

  function handleSignatureUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (signatureUrl) {
      URL.revokeObjectURL(signatureUrl);
    }

    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") {
        setSignatureUrl(reader.result);
      }
    });
    reader.readAsDataURL(file);
    event.target.value = "";
  }

  const getSignatureContext = useCallback(() => {
    return signatureCanvasRef.current?.getContext("2d") || null;
  }, []);

  const drawSignatureStroke = useCallback((points: SignaturePoint[]) => {
    const context = getSignatureContext();
    if (!context || points.length === 0) {
      return;
    }

    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = SIGNATURE_INK;
    context.lineWidth = 3;

    if (points.length === 1) {
      context.beginPath();
      context.arc(points[0].x, points[0].y, 1.5, 0, Math.PI * 2);
      context.fillStyle = SIGNATURE_INK;
      context.fill();
      return;
    }

    context.beginPath();
    context.moveTo(points[0].x, points[0].y);

    for (let index = 1; index < points.length - 1; index += 1) {
      const midpoint = {
        x: (points[index].x + points[index + 1].x) / 2,
        y: (points[index].y + points[index + 1].y) / 2,
      };
      context.quadraticCurveTo(points[index].x, points[index].y, midpoint.x, midpoint.y);
    }

    const last = points[points.length - 1];
    context.lineTo(last.x, last.y);
    context.stroke();
  }, [getSignatureContext]);

  const redrawSignatureCanvas = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    const context = getSignatureContext();
    if (!canvas || !context) {
      return;
    }

    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.restore();

    signatureStrokesRef.current.forEach(drawSignatureStroke);
    drawSignatureStroke(activeSignatureStrokeRef.current);
  }, [drawSignatureStroke, getSignatureContext]);

  const resizeSignatureCanvas = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }

    const pixelRatio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = Math.round(rect.width * pixelRatio);
    canvas.height = Math.round(rect.height * pixelRatio);

    const context = getSignatureContext();
    if (!context) {
      return;
    }

    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    redrawSignatureCanvas();
  }, [getSignatureContext, redrawSignatureCanvas]);

  function getSignaturePoint(event: PointerEvent<HTMLCanvasElement>): SignaturePoint {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  function handleSignaturePointerDown(event: PointerEvent<HTMLCanvasElement>) {
    event.preventDefault();
    drawingSignatureRef.current = true;
    activeSignatureStrokeRef.current = [getSignaturePoint(event)];
    event.currentTarget.setPointerCapture(event.pointerId);
    redrawSignatureCanvas();
  }

  function handleSignaturePointerMove(event: PointerEvent<HTMLCanvasElement>) {
    if (!drawingSignatureRef.current) {
      return;
    }

    activeSignatureStrokeRef.current = [...activeSignatureStrokeRef.current, getSignaturePoint(event)];
    redrawSignatureCanvas();
  }

  function finishSignatureStroke(event: PointerEvent<HTMLCanvasElement>) {
    if (!drawingSignatureRef.current) {
      return;
    }

    drawingSignatureRef.current = false;
    if (activeSignatureStrokeRef.current.length > 0) {
      signatureStrokesRef.current = [...signatureStrokesRef.current, activeSignatureStrokeRef.current];
      activeSignatureStrokeRef.current = [];
      setSignatureHasInk(true);
    }
    event.currentTarget.releasePointerCapture(event.pointerId);
    redrawSignatureCanvas();
  }

  function clearDrawnSignature() {
    signatureStrokesRef.current = [];
    activeSignatureStrokeRef.current = [];
    setSignatureHasInk(false);
    redrawSignatureCanvas();
  }

  function undoDrawnSignature() {
    signatureStrokesRef.current = signatureStrokesRef.current.slice(0, -1);
    setSignatureHasInk(signatureStrokesRef.current.length > 0);
    redrawSignatureCanvas();
  }

  function saveDrawnSignature() {
    const canvas = signatureCanvasRef.current;
    if (!canvas || !signatureHasInk) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const svg = signatureStrokesToSvg(signatureStrokesRef.current, rect.width || canvas.width, rect.height || canvas.height);

    if (signatureUrl) {
      URL.revokeObjectURL(signatureUrl);
    }

    setSignatureUrl(svgToDataUrl(svg));
    setSignatureDrawerOpen(false);
  }

  useEffect(() => {
    if (!signatureDrawerOpen) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(resizeSignatureCanvas);
    window.addEventListener("resize", resizeSignatureCanvas);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", resizeSignatureCanvas);
    };
  }, [signatureDrawerOpen, resizeSignatureCanvas]);

  const selectedFontSize = selectedElement?.style?.fontSize || 15;

  function addElement(type: PdfEditorElementType, label: string, value: string, assetPath: string | null = null) {
    if (!previewUrl) {
      return;
    }

    requestSeq.current += 1;

    editorRef.current?.addSticker({
      id: requestSeq.current,
      type,
      value: type === "mask" ? "" : value || label,
      assetPath,
    });
  }

  function addCustomField() {
    const nextNumber = customFields.length + 1;

    setCustomFields((current) => [
      ...current,
      {
        id: createLocalId("field"),
        label: `Pole ${nextNumber}`,
        value: "",
      },
    ]);
  }

  function updateCustomField(id: string, patch: Partial<Pick<CustomField, "label" | "value">>) {
    setCustomFields((current) => current.map((field) => (field.id === id ? { ...field, ...patch } : field)));
  }

  function removeCustomField(id: string) {
    setCustomFields((current) => current.filter((field) => field.id !== id));
  }

  async function saveElements() {
    if (!sessionId) {
      return false;
    }

    setSaveStatus("saving");
    const response = await fetch(`/api/pdf/sessions/${sessionId}/elements`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ elements }),
    }).catch(() => null);

    if (!response?.ok) {
      setSaveStatus("error");
      return false;
    }

    setSaveStatus("saved");
    return true;
  }

  async function openPreview() {
    const saved = await saveElements();
    if (saved) {
      setPreviewOpen(true);
    }
  }

  async function startCheckout() {
    if (checkoutStatus === "loading") {
      return;
    }

    if (!email) {
      loginWithPopup();
      return;
    }

    if (!sessionId) {
      return;
    }

    if (demoMode && !convertedFromDemo) {
      await convertDemoAndCheckout(sessionId);
      return;
    }

    if (exportUnlocked || role === "admin" || priceLabel === "0 PLN") {
      setExportUnlocked(true);
      downloadPdf();
      return;
    }

    const saved = await saveElements();
    if (!saved) {
      return;
    }

    setCheckoutStatus("loading");
    const response = await fetch("/api/payments/checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId }),
    }).catch(() => null);

    setCheckoutStatus("idle");

    const contentType = response?.headers.get("content-type") || "";
    const payload = contentType.includes("application/json") ? await response?.json().catch(() => null) : null;

    if (!response?.ok) {
      alert(payload?.error || "Nie udalo sie utworzyc platnosci Stripe.");
      return;
    }

    if (payload?.downloadUrl) {
      setExportUnlocked(true);
      window.location.assign(payload.downloadUrl);
      return;
    }

    if (payload?.redirectUrl) {
      window.location.assign(payload.redirectUrl);
      return;
    }

    alert("Stripe nie zwrocil linku platnosci.");
  }

  async function convertDemoAndCheckout(demoSessionId: string) {
    const saved = await saveElements();
    if (!saved) {
      return;
    }

    setCheckoutStatus("loading");
    const response = await fetch(`/api/pdf/sessions/${demoSessionId}/convert-demo`, {
      method: "POST",
    }).catch(() => null);

    const contentType = response?.headers.get("content-type") || "";
    const payload = contentType.includes("application/json") ? await response?.json().catch(() => null) : null;

    if (!response?.ok || !payload?.sessionId) {
      setCheckoutStatus("idle");
      alert(payload?.error || demoDownloadBlockedMessage());
      return;
    }

    setConvertedFromDemo(true);
    setExportUnlocked(role === "admin");
    setUpload({
      status: "ready",
      fileName: "PDF z demo",
      sessionId: payload.sessionId,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    });
    setPreviewUrl(`/api/pdf/sessions/${payload.sessionId}/source`);
    window.history.replaceState({}, "", `?s=${payload.sessionId}`);

    const checkoutResponse = await fetch("/api/payments/checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId: payload.sessionId }),
    }).catch(() => null);

    setCheckoutStatus("idle");

    const checkoutContentType = checkoutResponse?.headers.get("content-type") || "";
    const checkoutPayload = checkoutContentType.includes("application/json")
      ? await checkoutResponse?.json().catch(() => null)
      : null;

    if (!checkoutResponse?.ok) {
      alert(checkoutPayload?.error || "Nie udalo sie utworzyc platnosci Stripe.");
      return;
    }

    if (checkoutPayload?.downloadUrl) {
      setExportUnlocked(true);
      window.location.assign(checkoutPayload.downloadUrl);
      return;
    }

    if (checkoutPayload?.redirectUrl) {
      window.location.assign(checkoutPayload.redirectUrl);
      return;
    }

    alert("Stripe nie zwrocil linku platnosci.");
  }

  async function downloadPdf() {
    if (!sessionId) return;
    if (demoMode && !convertedFromDemo) {
      alert(demoDownloadBlockedMessage());
      return;
    }
    const saved = await saveElements();
    if (!saved) return;
    
    // Wyzwól pobieranie
    window.location.href = `/api/pdf/sessions/${sessionId}/export`;
  }

  async function sendAdminPaymentEmail() {
    if (!sessionId || role !== "admin" || adminEmailStatus === "sending") {
      return;
    }

    setAdminEmailStatus("sending");
    const response = await fetch("/api/admin/send-payment-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId }),
    }).catch(() => null);

    setAdminEmailStatus(response?.ok ? "sent" : "error");
  }

  async function sendPdfToMyEmail() {
    if (!sessionId || pdfEmailStatus === "sending") {
      return;
    }

    if (!email) {
      loginWithPopup();
      return;
    }

    if (demoMode && !convertedFromDemo) {
      await convertDemoAndCheckout(sessionId);
      return;
    }

    const saved = await saveElements();
    if (!saved) {
      return;
    }

    setPdfEmailStatus("sending");
    const response = await fetch(`/api/pdf/sessions/${sessionId}/email`, {
      method: "POST",
    }).catch(() => null);

    const contentType = response?.headers.get("content-type") || "";
    const payload = contentType.includes("application/json") ? await response?.json().catch(() => null) : null;

    if (!response?.ok) {
      setPdfEmailStatus("error");
      alert(payload?.error || "Nie udało się wysłać PDF-a na email.");
      return;
    }

    setPdfEmailStatus("sent");
  }

  function deleteSelectedElement() {
    if (selectionCount > 1) {
      editorRef.current?.deleteSelected();
      setSelectedElement(null);
      setSelectionCount(0);
      return;
    }

    if (!selectedElement) {
      return;
    }

    editorRef.current?.deleteElement(selectedElement.id);
    setElements((current) => current.filter((element) => element.id !== selectedElement.id));
    setSelectedElement(null);
  }

  const handleElementsChange = useCallback((nextElements: PdfEditorElement[]) => {
    setElements(nextElements);
    setSaveStatus("idle");
  }, []);

  const handleElementAdded = useCallback((element: PdfEditorElement) => {
    const detail =
      element.type === "mask"
        ? "Biała maska jest pod polami"
        : element.type === "signature"
          ? "Podpis trafił na PDF"
          : `"${element.value || "Pole"}" jest na dokumencie`;

    setToast({
      id: Date.now(),
      title: "Element dodany",
      detail,
    });
  }, []);

  const shouldWatermarkPreview = !exportUnlocked;

  function updateSelectedElement(patch: Partial<Pick<PdfEditorElement, "value" | "width" | "height" | "style">>) {
    if (!selectedElement) {
      return;
    }

    editorRef.current?.updateElement(selectedElement.id, patch);
    setSelectedElement({
      ...selectedElement,
      ...patch,
      style: {
        ...selectedElement.style,
        ...patch.style,
      },
    });
  }

  return (
    <div className="app-shell">
      <aside className="sidebar glass">
        <a href={marketingUrl} className="brand">
          <BrandLogo subtitle="Szybki generator PDF" />
        </a>

        <section className="panel-section">
          <h2>Upload</h2>
          {demoMode ? (
            <p className="status-line ok">
              <FileCheck2 size={16} />
              Tryb demo: możesz edytować i zobaczyć podgląd, ale pobranie PDF jest zablokowane.
            </p>
          ) : null}
          <label
            className="upload-box"
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
          >
            <FileUp size={24} />
            <span>{upload.status === "loading" ? "Wgrywam PDF..." : "Wybierz albo upuść PDF"}</span>
            <small>Max 10 MB</small>
            <input type="file" accept="application/pdf" onChange={handleUpload} />
          </label>
          {upload.status === "error" ? <p className="status-line error">{upload.message}</p> : null}
          {upload.status === "ready" ? (
            <p className="status-line ok">
              <FileCheck2 size={16} />
              {upload.fileName}
            </p>
          ) : null}
        </section>

        {upload.status === "ready" && !email ? (
          <section className="panel-section">
            <div className="login-nudge">
              <p>
                <strong>Zaloguj się przez Google</strong>, żeby zapisać dane (imię, nazwisko…) na kolejne wizyty i wysłać gotowy PDF na email.
              </p>
              <button className="primary" type="button" onClick={loginWithPopup}>
                <LogIn size={16} />
                Zaloguj przez Google
              </button>
            </div>
          </section>
        ) : null}

        <section className="panel-section">
          <h2>Dane do PDF</h2>
          <div className="field-stack">
            {insertableFields.map((field) => (
              <div className="insert-field" key={field.id}>
                <label>
                  {field.custom ? (
                    <input
                      className="field-name-input"
                      value={field.label}
                      onChange={(event) => updateCustomField(field.id, { label: event.target.value })}
                    />
                  ) : (
                    field.label
                  )}
                  <input
                    type={field.kind === "date" ? "date" : "text"}
                    placeholder={field.kind === "date" ? undefined : field.label}
                    value={field.value}
                    onChange={(event) =>
                      field.custom
                        ? updateCustomField(field.id, { value: event.target.value })
                        : setProfileValues((current) => ({
                            ...current,
                            [field.label]: event.target.value,
                          }))
                    }
                  />
                </label>
                <div className="insert-actions">
                  <button
                    className="icon-button"
                    type="button"
                    aria-label={`Dodaj ${field.label} do PDF`}
                    disabled={!previewUrl}
                    onClick={() => addElement(field.kind === "date" ? "date" : "text", field.label, field.value)}
                  >
                    <Plus size={17} />
                  </button>
                  {field.custom ? (
                    <button
                      className="icon-button"
                      type="button"
                      aria-label={`Usuń ${field.label}`}
                      onClick={() => removeCustomField(field.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
            <button className="secondary action-wide" type="button" onClick={addCustomField}>
              <Plus size={17} />
              Dodaj własne pole
            </button>
            <label>
              Podpis
              <span className="signature-upload">
                <PenLine size={16} />
                <span>{signatureUrl ? "Podpis dodany" : "Wgraj obraz podpisu"}</span>
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleSignatureUpload} />
              </span>
            </label>
            <button className="secondary action-wide signature-draw-mobile" type="button" onClick={() => setSignatureDrawerOpen(true)}>
              <PenLine size={17} />
              Narysuj palcem
            </button>
            {signatureUrl ? (
              <div className="signature-preview">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={signatureUrl} alt="Podgląd podpisu" />
              </div>
            ) : null}
            <button
              className="secondary action-wide"
              type="button"
              disabled={!previewUrl || !signatureUrl}
              onClick={() => addElement("signature", "Podpis", "Podpis", signatureUrl)}
            >
              <PenLine size={17} />
              Dodaj podpis do PDF
            </button>
          </div>
        </section>

        <section className="panel-section">
          <h2>Narzędzia</h2>
          <button className="sticker" type="button" disabled={!previewUrl} onClick={() => addElement("mask", "Biała maska", "")}>
            <Square size={15} />
            Biała maska
          </button>
        </section>
      </aside>

      <main className="workspace">
        <header className="topbar glass">
          {email ? (
            <div className="user-chip">
              <UserRound size={18} />
              <span>{email}</span>
              <b>{role}</b>
            </div>
          ) : (
            <button className="user-chip guest-btn" onClick={loginWithPopup}>
              <LogIn size={18} />
              <span>Zaloguj się</span>
            </button>
          )}
          <div className="toolbar-actions">
            <span className="price-chip">{priceLabel}</span>
            <button className="secondary" type="button" disabled={!canStartCheckout || saveStatus === "saving"} onClick={saveElements}>
              <Save size={18} />
              {saveStatus === "saving" ? "Zapisuję" : saveStatus === "saved" ? "Zapisano" : "Zapisz"}
            </button>
            <button className="secondary" type="button" disabled={!canStartCheckout} onClick={openPreview}>
              <Eye size={18} />
              Podgląd
            </button>
            <button className="primary" disabled={!canStartCheckout} onClick={startCheckout}>
              <Download size={18} />
              {canStartCheckout
                ? demoMode && !convertedFromDemo
                  ? email
                    ? "Przejdź do pobrania"
                    : "Zaloguj i pobierz"
                  : exportUnlocked
                    ? "Pobierz PDF"
                    : role === "admin"
                    ? "Pobierz (Darmowe dla Admina)"
                    : "Zapłać i pobierz"
                : "Najpierw wgraj PDF"}
            </button>
            <button
              className="secondary"
              type="button"
              disabled={!canStartCheckout || pdfEmailStatus === "sending"}
              onClick={sendPdfToMyEmail}
            >
              {pdfEmailStatus === "sending" ? "Wysyłam PDF" : pdfEmailStatus === "sent" ? "PDF wysłany" : "Wyślij na mój email"}
            </button>
            {role === "admin" ? (
              <button
                className="secondary"
                type="button"
                disabled={!sessionId || adminEmailStatus === "sending"}
                onClick={sendAdminPaymentEmail}
              >
                {adminEmailStatus === "sending" ? "Wysyłam email" : adminEmailStatus === "sent" ? "Email wysłany" : "Wyślij email"}
              </button>
            ) : null}
            {email && (
              <form action="/api/auth/logout" method="post">
                <button className="secondary" type="submit" aria-label="Wyloguj">
                  <LogOut size={18} />
                </button>
              </form>
            )}
          </div>
        </header>

        <section className="editor-grid">
          <div className="pdf-stage glass">
            {previewUrl ? (
              <PdfEditor
                ref={editorRef}
                fileUrl={previewUrl}
                sessionId={sessionId}
                onElementAdded={handleElementAdded}
                onElementsChange={handleElementsChange}
                onSelectElement={setSelectedElement}
                onSelectionCountChange={setSelectionCount}
              />
            ) : (
              <div className="empty-document">
                <FileUp size={42} />
                <h2>Wrzuć PDF, żeby zacząć</h2>
                <p>Tu pojawi się podgląd dokumentu i warstwa edycji.</p>
              </div>
            )}
          </div>

          <aside className="properties glass">
            <h2>Właściwości</h2>
            {selectionCount > 1 ? (
              <>
                <div className="tool-row active">
                  <Type size={18} />
                  Zaznaczono {selectionCount} elementy
                </div>
                <p className="hint">Przeciągnij jeden z zaznaczonych elementów albo użyj strzałek, żeby przesunąć całą grupę.</p>
                <button className="danger action-wide" type="button" onClick={deleteSelectedElement}>
                  <Trash2 size={18} />
                  Usuń zaznaczone
                </button>
              </>
            ) : selectedElement ? (
              <>
                <div className="tool-row active">
                  <Type size={18} />
                  {selectedElement.type === "mask"
                    ? "Biała maska"
                    : selectedElement.type === "signature"
                      ? "Podpis"
                      : selectedElement.type === "date"
                        ? "Data"
                        : "Tekst"}
                </div>
                {selectedElement.type !== "signature" && selectedElement.type !== "mask" ? (
                  <label>
                    Treść
                    <input
                      value={selectedElement.value}
                      onChange={(event) => updateSelectedElement({ value: event.target.value })}
                    />
                  </label>
                ) : null}
                {selectedElement.type !== "signature" && selectedElement.type !== "mask" ? (
                  <label>
                    Rozmiar fontu
                    <input
                      min="8"
                      max="72"
                      value={selectedFontSize}
                      type="number"
                      onChange={(event) =>
                        updateSelectedElement({ style: { fontSize: Number(event.target.value) || selectedFontSize } })
                      }
                    />
                  </label>
                ) : null}
                <label>
                  Szerokość
                  <input
                    min="10"
                    value={Math.round(selectedElement.width)}
                    type="number"
                    onChange={(event) => updateSelectedElement({ width: Number(event.target.value) || selectedElement.width })}
                  />
                </label>
                <label>
                  Wysokość
                  <input
                    min="10"
                    value={Math.round(selectedElement.height)}
                    type="number"
                    onChange={(event) => updateSelectedElement({ height: Number(event.target.value) || selectedElement.height })}
                  />
                </label>
                <button className="danger action-wide" type="button" onClick={deleteSelectedElement}>
                  <Trash2 size={18} />
                  Usuń element
                </button>
              </>
            ) : (
              <>
                <div className="tool-row">
                  <Type size={18} />
                  Brak zaznaczenia
                </div>
                <p className="hint">
                  Dodaj pole z lewego panelu. Ramka z kropek pokazuje dodany element; strzałki przesuwają zaznaczenie.
                </p>
              </>
            )}
            <p className="hint">Elementy na dokumencie: {elements.length}</p>
            {saveStatus === "error" ? <p className="status-line error">Nie udało się zapisać zmian.</p> : null}
          </aside>
        </section>
      </main>
      {previewOpen && previewUrl ? (
        <PdfPreviewModal
          elements={elements}
          fileUrl={previewUrl}
          priceLabel={priceLabel}
          role={role}
          watermarked={shouldWatermarkPreview}
          onCheckout={startCheckout}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
      {signatureDrawerOpen ? (
        <div className="signature-drawer-backdrop" role="dialog" aria-modal="true" aria-label="Narysuj podpis palcem">
          <div className="signature-drawer">
            <header className="signature-drawer-header">
              <span>
                <PenLine size={19} />
                <b>Narysuj podpis</b>
              </span>
              <button className="icon-button" type="button" aria-label="Zamknij podpis" onClick={() => setSignatureDrawerOpen(false)}>
                <X size={18} />
              </button>
            </header>
            <p className="signature-landscape-note">
              <Smartphone size={16} />
              Obróć telefon poziomo, jeśli chcesz mieć więcej miejsca na podpis.
            </p>
            <div className="signature-canvas-wrap">
              <div className="signature-baseline" aria-hidden="true">
                <span>podpisz tutaj</span>
              </div>
              <canvas
                ref={signatureCanvasRef}
                className="signature-canvas"
                aria-label="Pole rysowania podpisu"
                onPointerDown={handleSignaturePointerDown}
                onPointerMove={handleSignaturePointerMove}
                onPointerUp={finishSignatureStroke}
                onPointerCancel={finishSignatureStroke}
              />
            </div>
            <div className="signature-drawer-actions">
              <button className="secondary" type="button" onClick={clearDrawnSignature}>
                <Eraser size={17} />
                Wyczyść
              </button>
              <button className="secondary" type="button" disabled={!signatureHasInk} onClick={undoDrawnSignature}>
                <RotateCcw size={17} />
                Popraw
              </button>
              <button className="primary" type="button" disabled={!signatureHasInk} onClick={saveDrawnSignature}>
                <CheckCircle2 size={17} />
                Zapisz podpis
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {toast ? (
        <div className="action-toast" key={toast.id} role="status" aria-live="polite">
          <CheckCircle2 size={20} />
          <span>
            <b>{toast.title}</b>
            <small>{toast.detail}</small>
          </span>
        </div>
      ) : null}
    </div>
  );
}
